import jwt from "jwt-decode";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCookies } from "react-cookie";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { pulldownsPostData } from "../axios/api";
import createAxiosInstance from "../axios/index";
import {
  AUTH_ROUTES,
  PROVIDER_PORTAL_ROUTES,
} from "../constants/allRoutePaths";
import { DashboardActionTypes } from "../constants/tabs";
import { scoringIntegrationServiceStore } from "../store/service/scoringIntegrationService.store";
import { updatePatientLogin } from "../store/slices/patientLogInDataSlice";
import { updateSelectedPatient } from "../store/slices/selectedPatientSlice";
import { shouldShowAgreement } from "../utils/agreementUtils";
import { notificationToastSuccess } from "../utils/notification";
import { clearPatientData } from "../utils/patientUtils";
import roleAndPrivilegesFnc from "../utils/roleAndPriviledges";
import useSignInNavigation from "./useSignInNavigation";

const UserContext = createContext();
export const UserProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { navigateToSignInLanding } = useSignInNavigation();
  const [cookies, setCookies, removeCookie] = useCookies();
  const [error, setError] = useState("");
  const [latestVisitDmeStageNameId, setLatestVisitDmeStageNameId] =
    useState(null);
  const [latestVisitSleepStageNameId, setLatestVisitSleepStageNameId] =
    useState(null);
  const [isLatestVisitPage, setIsLatestVisitPage] = useState(false);

  const location = useLocation();

  const isPatientView =
    location.pathname === `/${PROVIDER_PORTAL_ROUTES.PATIENT_VIEW}`;

  useEffect(() => {
    if (!isPatientView) {
      setIsLatestVisitPage(false);
    }
  }, [isPatientView]);

  const shouldSendLatestVisitPage = isPatientView && isLatestVisitPage;

  const shouldSendLatestVisitPageRef = useRef(shouldSendLatestVisitPage);

  useEffect(() => {
    shouldSendLatestVisitPageRef.current = shouldSendLatestVisitPage;
  }, [shouldSendLatestVisitPage]);

  // Helper to check if we are on a page that should NOT trigger a redirect
  const isAuthPage = () => {
    const path = window.location.pathname;

    const normalizeUrl = (url) => url.replace(/\/$/, "");

    const isSleepNavigatorPage =
      normalizeUrl(window.location.href) ===
      normalizeUrl(AUTH_ROUTES.SLEEPNAVIGATOR_URL);

    return (
      [
        `/${AUTH_ROUTES.SIGNIN_INITIAL_PATIENT}`,
        `/${AUTH_ROUTES.SIGNIN_INITIAL_PROVIDER}`,
        `/${AUTH_ROUTES.SIGNIN_LANDING}`,
      ].includes(path) || isSleepNavigatorPage
    );
  };

  const getScoringIntegrationPulldown = async (payload) => {
    try {
      const response = await pulldownsPostData(
        axiosInstance,
        "scoringIntegrationServicesPulldown",
        payload,
      );

      const scoringIntegrationServiceArray = Array.isArray(response?.data)
        ? response?.data
        : [];

      const scoringProvidersArray = scoringIntegrationServiceArray.map(
        (row) => {
          return {
            ...row,
            tenantFlag: "enable" + row?.serviceName,
            minAge: 18,
          };
        },
      );
      scoringIntegrationServiceStore.setServices(scoringProvidersArray);
      return response;
    } catch (error) {
      console.error("Pulldown API error", error);
      scoringIntegrationServiceStore.setServices([]);
    }
  };

  const MFALogin = async (payload) => {
    let res;
    try {
      const { data } = await axiosInstance.post(`/api/login.php`, payload);

      res = data;
    } catch (ex) {
      setError(`Incorrect login or email.`);
      return;
    }
    if (res && res?.jwt && res?.message === "Successful login.") {
      setCookies("token", res.jwt); // your token
      setCookies("refreshToken", res.refreshToken);
      const { tabPath, tempPassword, userInfo } = roleAndPrivilegesFnc(res.jwt);

      sessionStorage.removeItem("hasReloaded");
      localStorage.setItem("tempPassErr", tempPassword);
      if (userInfo?.emailConfirmed === 0 && userInfo?.phoneNumConfirmed === 0) {
        navigate("/profile");
      } else {
        navigate(tabPath || "/dashboard");
      }
      localStorage.removeItem("patientId"); // cleanup for existing patientId in localStorage
    } else setError(res?.message);
  };

  const login = async (loginPayload, setIsAgreeSaving) => {
    let res;

    const { isAgreementPage, fetchUserAgreementPdf, ...payload } = loginPayload;

    try {
      const params = new URLSearchParams();

      if (payload?.userAgreementSigned) {
        params.set("saveUserAgreement", "1");
      }

      if (fetchUserAgreementPdf) {
        params.set("userAgreement", "1");
      }

      const queryString = params.toString();

      const { data } = await axiosInstance.post(
        `/api/login.php${queryString ? `?${queryString}` : ""}`,
        payload,
      );

      res = data;
    } catch (ex) {
      setError(`Incorrect login or email.`);

      handleAgreementSaving(loginPayload, setIsAgreeSaving);
      return;
    }
    if (res && res?.jwt && res?.message === "Successful login.") {
      setCookies("token", res.jwt); // your token
      setCookies("refreshToken", res.refreshToken);
      const { tabPath, tempPassword } = roleAndPrivilegesFnc(res.jwt);

      sessionStorage.removeItem("hasReloaded");
      navigate(tabPath || "/dashboard");
      localStorage.removeItem("patientId"); // cleanup for existing patientId in localStorage
      localStorage.setItem("tempPassErr", tempPassword);
      window.location.reload();

      return true;
    } else if (res?.response === 1 && res?.securityCodeSend === "1") {
      navigate("/authlogin", { state: { ...res, tryAgainPayload: payload } });
    } else if (
      res?.response === 1 &&
      (res?.listTenants === 1 || res?.selectedTenantSignIn === 1)
    ) {
      if (handleUserAgreement(res, loginPayload)) return;

      const isUserAgreementSigned =
        loginPayload?.isAgreementPage &&
        payload?.userAgreementSigned &&
        res?.userAgreementSigned;

      if (isUserAgreementSigned) {
        notificationToastSuccess(res?.message);
      }

      navigate(`/${AUTH_ROUTES.SIGNIN}`, {
        state: res,
        replace: isUserAgreementSigned,
      });
    } else if (res?.response === 1 && res?.setupSendSecurityCode === "1") {
      navigate("/setup-mfa", { state: res });
    } else if (loginPayload?.fetchUserAgreementPdf && res?.file) {
      return res;
    } else {
      setError(res?.message);

      handleAgreementSaving(loginPayload, setIsAgreeSaving);
    }
  };

  const emailSignin = async ({ email, user }) => {
    let res;
    try {
      // setError('')
      const { data } = await axiosInstance.post(
        user === "Provider"
          ? `/api/checkTenant.php`
          : `/api/checkTenantPatient.php`,
        {
          email: email,
        },
      );

      res = data;
    } catch (ex) {
      setError(`Incorrect login or email.`);
      return;
    }
    if (res?.response === 1 && (res?.listTenants === 1 || res?.tenantId)) {
      const route =
        user === "Provider"
          ? `/${AUTH_ROUTES.SIGNIN}`
          : `/${AUTH_ROUTES.SIGNIN_AS_PATIENT}`;

      if (user === "Provider" && handleUserAgreement(res)) return;
      if (user !== "Provider" && handlePatientAgreement(res)) return;

      navigate(route, { state: res });
    } else {
      setError(res?.message);
    }
  };

  const handleUserAgreement = (res, loginPayload) => {
    const { selectedTenant } = loginPayload || {};
    const { enableUserAgreement, userAgreementSigned } = res || {};

    const shouldShowUserAgreement = shouldShowAgreement({
      enableAgreement: enableUserAgreement,
      agreementSigned: userAgreementSigned,
    });

    if (shouldShowUserAgreement) {
      navigate(`/${AUTH_ROUTES.USER_AGREEMENT}`, {
        state: { selectedTenant, ...res },
      });
      return true;
    }

    return false;
  };

  const handlePatientAgreement = (res, loginPayload) => {
    const { selectedTenant } = loginPayload || {};
    const { enableUserAgreement, patientAgreementSigned } = res || {};

    const shouldShowPatientAgreement = shouldShowAgreement({
      enableAgreement: enableUserAgreement,
      agreementSigned: patientAgreementSigned,
    });

    if (shouldShowPatientAgreement) {
      navigate(`/${AUTH_ROUTES.PATIENT_AGREEMENT}`, {
        state: { selectedTenant, ...res },
      });
      return true;
    }

    return false;
  };

  const handlePatientLogIn = async (loginPayload, setIsAgreeSaving) => {
    let res;

    const { isAgreementPage, fetchPatientAgreementPdf, ...payload } =
      loginPayload;

    try {
      const params = new URLSearchParams();

      if (payload?.patientAgreementSigned) {
        params.set("saveUserAgreement", "1");
      }

      if (fetchPatientAgreementPdf) {
        params.set("patientAgreement", "1");
      }

      const queryString = params.toString();

      // setError('')
      const { data } = await axiosInstance.post(
        `/api/patientLogin.php${queryString ? `?${queryString}` : ""}`,
        payload,
      );

      res = data;
    } catch (ex) {
      setError(`Incorrect login or email.`);

      handleAgreementSaving(loginPayload, setIsAgreeSaving);
      return;
    }
    if (res && res?.jwt && res?.message === "Successful login.") {
      setCookies("token", res.jwt); // your token
      setCookies("isPatient", true);

      navigate("/patient-portal");

      sessionStorage.removeItem("hasReloaded");
      const { id, tempPassword } = roleAndPrivilegesFnc(res.jwt);
      dispatch(updatePatientLogin(id));
      localStorage.setItem("tempPassErr", tempPassword);
      window.location.reload();
    } else if (
      res?.response === 1 &&
      (res?.listTenants === 1 || res?.selectedTenantSignIn === 1)
    ) {
      if (handlePatientAgreement(res, loginPayload)) return;

      const isPatientAgreementSigned =
        loginPayload?.isAgreementPage &&
        payload?.patientAgreementSigned &&
        res?.patientAgreementSigned;

      if (isPatientAgreementSigned) {
        notificationToastSuccess(res?.message);
      }

      navigate(`/${AUTH_ROUTES.SIGNIN_AS_PATIENT}`, {
        state: res,
        replace: isPatientAgreementSigned,
      });
    } else if (res?.response === 1 && res?.setupSendSecurityCode === "1") {
      navigate("/setup-mfa", { state: res });
    } else if (loginPayload?.fetchPatientAgreementPdf && res?.file) {
      return res;
    } else {
      setError(res?.message);

      handleAgreementSaving(loginPayload, setIsAgreeSaving);
    }
  };

  const handleAgreementSaving = (payload, setIsAgreeSaving) => {
    if (payload?.isAgreementPage) {
      setIsAgreeSaving(false);
    }
  };

  const handleLogout = async (
    shouldRedirect = true,
    setIsLoggingOut,
    isLoggingOut,
  ) => {
    if (isLoggingOut) return;

    if (typeof setIsLoggingOut === "function") {
      setIsLoggingOut(true);
    }

    await logout(shouldRedirect, true);
  };

  const logout = async (shouldRedirect = true, callLogoutEndpoint = false) => {
    if (callLogoutEndpoint) {
      const token = getCookie("token");

      try {
        await axiosInstance.post(
          `/api/logOut.php`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } catch (ex) {
        console.error(ex);
      }
    }

    ["token", "isPatient", "refreshToken"].forEach((obj) => removeCookie(obj)); // remove data save in cookies
    localStorage.removeItem("lastActivity");
    clearPatientData(undefined, false);
    localStorage.removeItem("tempPassErr");
    localStorage.removeItem("hasDismissedPasswordDialog");
    localStorage.removeItem(DashboardActionTypes.DASHBOARD_FILTERS_KEY);
    localStorage.setItem("logout", Date.now());
    dispatch(updateSelectedPatient(undefined));

    const currentlyOnLogin = isAuthPage();

    if (shouldRedirect && !currentlyOnLogin) {
      navigateToSignInLanding();
    }
  };

  // Inside UserProvider.js
  const isTokenValid = (token, bufferSeconds = 5) => {
    if (!token) return false;
    try {
      const decoded = jwt(token);
      // Check if token has at least 5 seconds left before expiry
      return decoded.exp * 1000 > Date.now() + bufferSeconds * 1000;
    } catch (e) {
      return false;
    }
  };

  const axiosInstance = useMemo(() => {
    const instance = createAxiosInstance();
    // Configure the interceptor here
    instance.interceptors.request.use(
      async (config) => {
        const token = cookies.token;

        // if (config.data instanceof FormData) {
        //   config.data.append("latestVisit", "1");
        // } else {
        //   config.data = {
        //     ...config.data,
        //     latestVisit: 1,
        //   };
        // }

        if (token && !isAuthPage()) {
          const decoded = jwt(token);
          const isExpired = decoded.exp * 1000 < Date.now();

          if (isExpired && !cookies.isPatient) {
            const refreshToken = cookies.refreshToken;
            const newToken = await refreshAccessToken(
              refreshToken,
              cookies.token,
            );
            config.headers["Authorization"] = `Bearer ${newToken}`;
          } else {
            config.headers["Authorization"] = `Bearer ${token}`;
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    return instance;
  }, [cookies.token]);

  // Function to refresh access token
  const refreshAccessToken = async (refreshToken) => {
    const instance = createAxiosInstance();
    const skipRedirect = isAuthPage();

    let res;
    try {
      await instance
        .post(
          "/api/refreshToken.php",
          { refreshToken },
          {
            headers: {
              Authorization: `Bearer ${cookies.token}`,
            },
          },
        )
        .then((response) => {
          if (response.data.accessToken) {
            setCookies("token", response?.data?.accessToken);
            res = response.data.accessToken;
          } else {
            logout(!skipRedirect);
          }
        });
      return res;
    } catch (ex) {
      logout(!skipRedirect);
    }
  };

  const getCookie = (name) => {
    const cookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`));

    return cookie ? cookie.split("=")[1] : undefined;
  };

  const value = useMemo(
    () => ({
      cookies,
      error,
      login,
      logout,
      handleLogout,
      handlePatientLogIn,
      setError,
      MFALogin,
      axiosInstance,
      emailSignin,
      latestVisitDmeStageNameId,
      latestVisitSleepStageNameId,
      setLatestVisitDmeStageNameId,
      setLatestVisitSleepStageNameId,
      getScoringIntegrationPulldown,
      isTokenValid,
      getCookie,
      setIsLatestVisitPage,
    }),
    [cookies, error, latestVisitDmeStageNameId, latestVisitSleepStageNameId],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useAuth = () => {
  return useContext(UserContext);
};

export const useAxios = () => {
  const auth = useAuth();
  // if (auth) {
  //   axios.defaults.headers.common["Authorization"] =
  //     "Bearer " + auth.cookies.token;
  // }

  return auth.axiosInstance;
};
